import { useDeepCompareEffect } from "@react-hookz/web";
import { type DependencyList, useCallback, useRef, useState } from "react";
import type { Database } from "../../../__definitions__/database-definitions";
import {
  type SupabaseHandlerArg,
  type SupabaseSchemaName,
  type UseSupabaseLoaderState,
} from "./types";
import { useSupabase } from "./use-supabase";
export class Waiting extends Error {}

/**
 * An Effect for loading data from Supabase with a reload handler; may redirect if not authenticated.
 */
export function useSupabaseLoader<T, SchemaName extends SupabaseSchemaName<Database> = "public">(
  callback: (arg0: SupabaseHandlerArg<SchemaName>) => Promise<T>,
  deps: DependencyList,
  schema: SchemaName = "public" as SchemaName,
): UseSupabaseLoaderState<T> & { reload: () => void } {
  const [reloadCount, setReloadCount] = useState(0);
  const [state, setState] = useState<UseSupabaseLoaderState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  const latestRequestId = useRef(0);

  useSupabase(
    async (ctx) => {
      const requestId = ++latestRequestId.current;
      if (!ctx) return setState({ data: null, loading: true, error: null });
      if (!state.loading) return;
      if (reloadCount === 0) return;

      try {
        const data = await callback(ctx);
        if (!ctx.abortController?.signal.aborted && requestId === latestRequestId.current)
          setState({ data, loading: false, error: null });
      } catch (error) {
        if (!ctx.abortController?.signal.aborted && requestId === latestRequestId.current)
          if (!(error instanceof Waiting))
            setState({ data: null, loading: false, error: error as Error });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.loading, reloadCount], // Do not add callback
    schema,
  );

  const reload = useCallback(() => {
    setState({ data: null, loading: true, error: null });
    setReloadCount((old) => old + 1);
  }, []);

  useDeepCompareEffect(reload, deps);

  return { ...state, reload };
}
